import { overallDoValor } from './financas';
import type { Posicao } from './tipos';
import { ELENCOS_LIGAS } from './ligas-mundo-elencos';
import { ELENCOS_SERIE_A_ITALIA } from './liga-italia-elencos';
import { SERIE_A_ITALIA } from './liga-italia';

/**
 * Ligas estrangeiras da aba "internacional" das Tabelas, agrupadas por país. Por enquanto é só cadastro (clubes, jogadores,
 * posição, idade e valor de mercado em milhões; €1 mi = R$ 1 mi): o jogo ainda não tem força nem calendário dessas ligas.
 */
export interface LigaMundo {
  id: string;
  nome: string;
}
export interface PaisMundo {
  nome: string;
  ligas: LigaMundo[];
}
/** Jogador: [nome, posição, idade (0 = desconhecida), valor em milhões (0 = desconhecido)] */
export type JogadorMundo = [string, string, number, number];
export interface ClubeMundo {
  nome: string;
  estadio?: string;
  cidade?: string;
  elenco: JogadorMundo[];
  /** média da força dos 11 melhores jogadores com valor conhecido (undefined se a fonte não traz valores) */
  forca?: number;
}

export const PAISES_MUNDO: PaisMundo[] = [
  { nome: 'Alemanha', ligas: [{ id: 'de-1', nome: 'Bundesliga' }, { id: 'de-2', nome: '2. Bundesliga' }] },
  { nome: 'Arábia Saudita', ligas: [{ id: 'sa-1', nome: 'Saudi Pro League' }] },
  { nome: 'Argentina', ligas: [{ id: 'ar-1', nome: 'Torneo Clausura' }] },
  { nome: 'Espanha', ligas: [{ id: 'es-1', nome: 'La Liga' }, { id: 'es-2', nome: 'La Liga 2' }] },
  { nome: 'Estados Unidos', ligas: [{ id: 'us-1', nome: 'Major League Soccer' }] },
  { nome: 'França', ligas: [{ id: 'fr-1', nome: 'Ligue 1' }, { id: 'fr-2', nome: 'Ligue 2' }] },
  { nome: 'Inglaterra', ligas: [{ id: 'en-1', nome: 'Premier League' }, { id: 'en-2', nome: 'Championship' }] },
  { nome: 'Itália', ligas: [{ id: 'it-a', nome: 'Série A' }, { id: 'it-b', nome: 'Série B' }] },
];

/** Ordem em que as posições aparecem na lista do elenco. */
const ORDEM_POSICOES = ['Goleiro', 'Zagueiro', 'Lateral Esq.', 'Lateral Dir.', 'Volante', 'Meia Central', 'Meio-Campo', 'Meia Direita', 'Meia Esquerda', 'Meia Ofensivo', 'Ponta-Esquerda', 'Ponta-Direita', 'Seg. Atacante', 'Atacante', 'Centroavante'];

const POSICAO_DO_NOME: Record<string, Posicao> = { Goleiro: 'G', Zagueiro: 'Z', 'Lateral Esq.': 'L', 'Lateral Dir.': 'L', Volante: 'M', 'Meia Central': 'M', 'Meio-Campo': 'M', 'Meia Direita': 'M', 'Meia Esquerda': 'M', 'Meia Ofensivo': 'M', 'Ponta-Esquerda': 'A', 'Ponta-Direita': 'A', 'Seg. Atacante': 'A', Atacante: 'A', Centroavante: 'A' };

/** Força do jogador pelo valor de mercado (a mesma curva da Série A e B brasileiras: overallDoValor; €1 mi = R$ 1 mi). null se a fonte não traz o valor. */
export function forcaDoJogador(j: JogadorMundo): number | null {
  const [, pos, idade, valor] = j;
  if (valor <= 0) return null;
  return overallDoValor(valor * 1_000_000, idade > 0 ? idade : 26, POSICAO_DO_NOME[pos]);
}

function forcaDoClube(elenco: JogadorMundo[]): number | undefined {
  const f = elenco.map(forcaDoJogador).filter((x): x is number => x !== null).sort((a, b) => b - a).slice(0, 11);
  return f.length ? Math.round(f.reduce((s, x) => s + x, 0) / f.length) : undefined;
}

function ordenar(lista: JogadorMundo[]): JogadorMundo[] {
  const ordem = (p: string): number => {
    const i = ORDEM_POSICOES.indexOf(p);
    return i < 0 ? ORDEM_POSICOES.length : i;
  };
  return [...lista].sort((a, b) => ordem(a[1]) - ordem(b[1]) || b[3] - a[3]);
}

/** Nome da liga pelo id ("en-1" -> "Premier League"). */
export function nomeDaLiga(id: string): string {
  return PAISES_MUNDO.flatMap((p) => p.ligas).find((l) => l.id === id)?.nome ?? id;
}

export function paisDaLiga(id: string): PaisMundo | undefined {
  return PAISES_MUNDO.find((p) => p.ligas.some((l) => l.id === id));
}

/** Clubes da liga (em ordem alfabética) com o elenco de cada um agrupado por posição. */
export function clubesDaLiga(id: string): ClubeMundo[] {
  if (id === 'it-a') {
    return SERIE_A_ITALIA.map((c) => {
      const elenco = ordenar((ELENCOS_SERIE_A_ITALIA[c.nome] ?? []).map(([n, p, i, v]): JogadorMundo => [n, p, i, v]));
      return { nome: c.nome, estadio: c.estadio, cidade: c.cidade, elenco, forca: forcaDoClube(elenco) };
    });
  }
  const clubes = ELENCOS_LIGAS[id] ?? {};
  return Object.keys(clubes)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'))
    .map((nome) => {
      const elenco = ordenar(clubes[nome] ?? []);
      return { nome, elenco, forca: forcaDoClube(elenco) };
    });
}

/** Jogador de uma liga estrangeira, achatado para a Procura (só consulta: ainda não dá para contratá-lo). */
export interface JogadorExterno {
  /** id negativo e estável, para não colidir com os ids dos jogadores do jogo */
  id: number;
  ligaId: string;
  liga: string;
  pais: string;
  clube: string;
  nome: string;
  posicaoNome: string;
  posicao: Posicao | undefined;
  lado: 'E' | 'D' | '';
  idade: number;
  /** valor em milhões (0 = desconhecido) */
  valorMi: number;
  forca: number | null;
}

let cacheExternos: JogadorExterno[] | null = null;

/** Todos os jogadores das ligas estrangeiras cadastradas (cache; ids -1, -2, ...). */
export function jogadoresExternos(): JogadorExterno[] {
  if (cacheExternos) return cacheExternos;
  const lista: JogadorExterno[] = [];
  for (const pais of PAISES_MUNDO) {
    for (const liga of pais.ligas) {
      for (const clube of clubesDaLiga(liga.id)) {
        for (const j of clube.elenco) {
          const nomePos = j[1];
          lista.push({
            id: -(lista.length + 1),
            ligaId: liga.id,
            liga: liga.nome,
            pais: pais.nome,
            clube: clube.nome,
            nome: j[0],
            posicaoNome: nomePos,
            posicao: POSICAO_DO_NOME[nomePos],
            lado: /Esq/.test(nomePos) ? 'E' : /Dir/.test(nomePos) ? 'D' : '',
            idade: j[2],
            valorMi: j[3],
            forca: forcaDoJogador(j),
          });
        }
      }
    }
  }
  cacheExternos = lista;
  return lista;
}
